import * as PIXI from "pixi.js";
import { _section } from "./_section";

export type uniforms = {
    color1?: PIXI.Color | string | null;
    color2?: PIXI.Color | string | null;
    color3?: PIXI.Color | string | null;
    color4?: PIXI.Color | string | null;
    color5?: PIXI.Color | string | null;
    colorSize?: number;
    colorSpacing?: number;
    colorRotation?: number;
    colorSpread?: number;
    colorOffset?: Float32Array | number[];
    transformPosition?: Float32Array | number[];
    zoom?: number;
    spacing?: number;
    displacement?: number;
    seed?: number;
    noiseSize?: number;
    noiseIntensity?: number;

    viewportSize?: Float32Array | number[];
};

type BackgroundGradientEventCallback = (instance: _backgroundGradient, event: MouseEvent) => void;

export type backgroundGradientProps = {
    target?: HTMLElement;
    uniforms?: uniforms;
    debug?: boolean;
    events?: {
        [key: string]: BackgroundGradientEventCallback;
    };
};

export class _backgroundGradient {
    target: HTMLElement | null;
    app: PIXI.Application | null = null;
    shaderTemplate: (() => string) | null = null;
    vertex: string | null = null;
    fragment: string | null = null;
    defaults: any;
    custom: any = null;
    uniforms: uniforms;
    geometry: PIXI.Geometry | null = null;
    mouse = { x: 0, y: 0 };
    lerpedMouse = { x: 0, y: 0 };
    updateTime = 0;
    debug: boolean;
    shader: PIXI.Shader | null = null;
    quad: PIXI.Mesh<PIXI.Geometry> | null = null;

    events: { [key: string]: BackgroundGradientEventCallback };
    listeners: { [key: string]: BackgroundGradientEventCallback[] };

    constructor(props: backgroundGradientProps) {
        this.target = props.target ?? null;

        this.app = null;

        this.shaderTemplate = null;
        this.vertex = null;
        this.fragment = null;

        this.defaults = {
            color1: props.uniforms?.color1 ?? "#3a518f", // blue (bg color)
            color2: props.uniforms?.color2 ?? "#8db6b9", // green (left edge color)
            color3: props.uniforms?.color3 ?? "#fafafa", // white (left middle color)
            color4: props.uniforms?.color4 ?? "#fafafa", // white (right middle color)
            color5: props.uniforms?.color5 ?? "#8db6b9", // green (right edge color)

            colorSize: props.uniforms?.colorSize ?? 0.5, // size of the other colors than main bg color
            colorSpacing: props.uniforms?.colorSpacing ?? 0.33, // other colors gap to each other
            colorRotation: props.uniforms?.colorRotation ?? 2, // gradient's rotation, 0 = 0deg,
            colorSpread: props.uniforms?.colorSpread ?? 30, // other color spread size
            colorOffset: props.uniforms?.colorOffset ?? [-2, -1], // gradient position

            transformPosition: props.uniforms?.transformPosition ?? [-2.5, -0.5], // container position

            zoom: props.uniforms?.zoom ?? 0.7,

            spacing: props.uniforms?.spacing ?? 4, // gradient is repeated, but needs spacing
            displacement: props.uniforms?.displacement ?? 3,
            seed: props.uniforms?.seed ?? 4000,

            // for noise
            noiseSize: props.uniforms?.noiseSize ?? 0.5,
            noiseIntensity: props.uniforms?.noiseIntensity ?? 0.04,
        };
        this.custom = null;

        this.uniforms = {
            color1: new PIXI.Color(this.defaults.color1), // bg color
            color2: new PIXI.Color(this.defaults.color2), // left edge color
            color3: new PIXI.Color(this.defaults.color3), // left middle color
            color4: new PIXI.Color(this.defaults.color4), // right middle color
            color5: new PIXI.Color(this.defaults.color5), // right edge color

            colorSize: this.defaults.colorSize, // size of the other colors than main bg color
            colorSpacing: this.defaults.colorSpacing, // other colors gap to each other
            colorRotation: this.defaults.colorRotation, // gradient's rotation, 0 = 0deg,
            colorSpread: this.defaults.colorSpread, // other color spread size
            colorOffset: new Float32Array(this.defaults.colorOffset),

            transformPosition: new Float32Array(this.defaults.transformPosition),

            zoom: this.defaults.zoom,

            spacing: this.defaults.spacing, // gradient is repeated, but needs spacing
            displacement: this.defaults.displacement,
            seed: this.defaults.seed,

            // for noise
            noiseSize: this.defaults.noiseSize,
            noiseIntensity: this.defaults.noiseIntensity,

            viewportSize: new Float32Array(
                this.target?.tagName.toLowerCase() === "body"
                    ? [window.innerWidth, window.innerHeight]
                    : [this.target.offsetWidth, this.target.offsetHeight],
            ),
            mousePosition: new Float32Array([0, 0]),
        };
        this.geometry = null;

        this.mouse = { x: 0, y: 0 };
        this.lerpedMouse = { x: 0, y: 0 };
        this.updateTime = 0;

        this.events = props?.events ?? {};
        this.listeners = {};

        this.debug = props.debug ?? false;

        this.init();
    }

    init() {
        if (!(window as any).backgroundGradient) (window as any).backgroundGradient = [];
        (window as any).backgroundGradient.push(this); // keep in array this instance

        // init pixi
        this.app = new PIXI.Application({
            antialias: true,
            backgroundAlpha: 0,
            resizeTo: window,
        });
        this.target?.prepend(this.app.view as HTMLCanvasElement);

        this.app.view.classList.add(
            "-z-0",
            ...(this.target?.tagName.toLowerCase() === "body" ? ["fixed"] : ["absolute"]),
            "top-0",
            "left-0",
            "w-full",
            "h-full",
            "pointer-events-none",
        );

        // for shader template
        this.shaderTemplate = ({
            hash: g = `
                            p = fract(p * vec3(.1031, .1030, .0973));
                            p += dot(p, p.yxz+33.33);
                            return fract((p.xxy + p.yxx)*p.zyx);
                        `,
        } = {}) => `
                    vec3 gradientDerivativesNoise3DHash(vec3 p) {
                        ${g}
                    }

                    // return value noise (in x) and its derivatives (in yzw)
                    vec4 gradientDerivativesNoise3D( in vec3 x ) {
                        // grid
                        vec3 p = floor(x);
                        vec3 w = fract(x);

                        #if 1
                            // quintic interpolant
                            vec3 u = w*w*w*(w*(w*6.0-15.0)+10.0);
                            vec3 du = 30.0*w*w*(w*(w-2.0)+1.0);
                        #else
                            // cubic interpolant
                            vec3 u = w*w*(3.0-2.0*w);
                            vec3 du = 6.0*w*(1.0-w);
                        #endif

                        // gradients
                        vec3 ga = gradientDerivativesNoise3DHash( p+vec3(0.0,0.0,0.0) );
                        vec3 gb = gradientDerivativesNoise3DHash( p+vec3(1.0,0.0,0.0) );
                        vec3 gc = gradientDerivativesNoise3DHash( p+vec3(0.0,1.0,0.0) );
                        vec3 gd = gradientDerivativesNoise3DHash( p+vec3(1.0,1.0,0.0) );
                        vec3 ge = gradientDerivativesNoise3DHash( p+vec3(0.0,0.0,1.0) );
                        vec3 gf = gradientDerivativesNoise3DHash( p+vec3(1.0,0.0,1.0) );
                        vec3 gg = gradientDerivativesNoise3DHash( p+vec3(0.0,1.0,1.0) );
                        vec3 gh = gradientDerivativesNoise3DHash( p+vec3(1.0,1.0,1.0) );

                        // projections
                        float va = dot( ga, w-vec3(0.0,0.0,0.0) );
                        float vb = dot( gb, w-vec3(1.0,0.0,0.0) );
                        float vc = dot( gc, w-vec3(0.0,1.0,0.0) );
                        float vd = dot( gd, w-vec3(1.0,1.0,0.0) );
                        float ve = dot( ge, w-vec3(0.0,0.0,1.0) );
                        float vf = dot( gf, w-vec3(1.0,0.0,1.0) );
                        float vg = dot( gg, w-vec3(0.0,1.0,1.0) );
                        float vh = dot( gh, w-vec3(1.0,1.0,1.0) );

                        // interpolations
                        return vec4( va + u.x*(vb-va) + u.y*(vc-va) + u.z*(ve-va) + u.x*u.y*(va-vb-vc+vd) + u.y*u.z*(va-vc-ve+vg) + u.z*u.x*(va-vb-ve+vf) + (-va+vb+vc-vd+ve-vf-vg+vh)*u.x*u.y*u.z,    // value
                                    ga + u.x*(gb-ga) + u.y*(gc-ga) + u.z*(ge-ga) + u.x*u.y*(ga-gb-gc+gd) + u.y*u.z*(ga-gc-ge+gg) + u.z*u.x*(ga-gb-ge+gf) + (-ga+gb+gc-gd+ge-gf-gg+gh)*u.x*u.y*u.z +   // derivatives
                                    du * (vec3(vb,vc,ve) - va + u.yzx*vec3(va-vb-vc+vd,va-vc-ve+vg,va-vb-ve+vf) + u.zxy*vec3(va-vb-ve+vf,va-vb-vc+vd,va-vc-ve+vg) + u.yzx*u.zxy*(-va+vb+vc-vd+ve-vf-vg+vh) ));
                    }
                `;

        // for vertex
        this.vertex = `
                    #version 300 es

                    in vec3 position;
                    in vec2 uv;

                    out vec2 vUV;
                    out vec2 vPosition;

                    void main() {
                        gl_Position = vec4(position, 1.0);
                        vPosition = position.xy;
                        vUV = uv;
                    }
                `;

        // for fragment
        this.fragment = `
                    #version 300 es
                    precision highp float;

                    uniform vec3 color1;
                    uniform vec3 color2;
                    uniform vec3 color3;
                    uniform vec3 color4;
                    uniform vec3 color5;
                    
                    uniform float colorSize;
                    uniform float colorSpacing;
                    uniform float colorRotation;
                    uniform float colorSpread;
                    uniform float displacement;
                    uniform float zoom;
                    uniform float spacing;
                    uniform float noiseSize;
                    uniform float seed;
                    uniform float noiseIntensity;

                    uniform vec2 viewportSize;
                    uniform vec2 colorOffset;
                    uniform vec2 transformPosition;
                    uniform vec2 mousePosition;

                    in vec2 vUV;
                    in vec2 vPosition;

                    out vec4 fragColor;

                    ${this.shaderTemplate()}

                    float hash(vec2 p) {
                        p = 50.0 * fract(p * 0.3183099 + vec2(0.71, 0.113));
                        return -1.0 + 2.0 * fract(p.x * p.y * (p.x + p.y));
                    }

                    float computeNoise(in vec2 p) {
                        vec2 i = floor(p);
                        vec2 f = fract(p);

                        vec2 u = f * f * (3.0 - 2.0 * f);

                        return mix(mix(hash(i + vec2(0.0, 0.0)),
                            hash(i + vec2(1.0, 0.0)), u.x),
                        mix(hash(i + vec2(0.0, 1.0)),
                            hash(i + vec2(1.0, 1.0)), u.x), u.y);
                    }

                    vec2 rotate(vec2 v, float a) {
                        float s = sin(a);
                        float c = cos(a);
                        mat2 m = mat2(c, -s, s, c);
                        return m * v;
                    }

                    void main() {
                        vec2 position = vPosition;
                        position += sin(position.yx * 1.0) * mousePosition * 0.1;
                        position.x *= min(1., viewportSize.x / viewportSize.y);
                        position.y *= min(1., viewportSize.y / viewportSize.x);
                        position /= zoom;
                        position += transformPosition;

                        vec2 noiseLocalPosition = position * .5 + .5;
                        vec3 displacementNoise = gradientDerivativesNoise3D(vec3(noiseLocalPosition, seed)).xyz;

                        float noise = computeNoise((vPosition * 0.5 + 0.5) * viewportSize / noiseSize);

                        position += displacementNoise.xz * displacement;

                        vec2 offsetedPosition = position;
                        offsetedPosition -= colorOffset;
                        offsetedPosition = mod(offsetedPosition - spacing, vec2(spacing * 2.)) - spacing;
                        offsetedPosition = rotate(offsetedPosition, -colorRotation);
                        offsetedPosition /= vec2(colorSize, colorSize);
                        offsetedPosition *= vec2(1. / colorSpread, 1.);

                        vec3 color = color1;
                        color = mix(color2, color, smoothstep(0., 1., distance(offsetedPosition, vec2(0., colorSpacing * 1.5))));
                        color = mix(color3, color, smoothstep(0., 1., distance(offsetedPosition, vec2(0., colorSpacing * .5))));
                        color = mix(color4, color, smoothstep(0., 1., distance(offsetedPosition, vec2(0., -colorSpacing * .5))));
                        color = mix(color5, color, smoothstep(0., 1., distance(offsetedPosition, vec2(0., -colorSpacing * 1.5))));

                        color += noise * noiseIntensity;
                        color = clamp(color, 0.0, 1.0);

                        fragColor = vec4(color, 1.0);
                    }
                `;

        this.geometry = new PIXI.Geometry()
            .addAttribute("position", [-1, -1, 1, -1, 1, 1, -1, 1], 2)
            .addAttribute("uv", [0, 0, 1, 0, 1, 1, 0, 1], 2)
            .addIndex([0, 1, 2, 0, 2, 3]);

        this.shader = PIXI.Shader.from(this.vertex, this.fragment, this.uniforms);
        this.quad = new PIXI.Mesh(this.geometry, this.shader);

        this.app.stage.addChild(this.quad);

        // for resize
        window.addEventListener("resize", () => {
            if (this.shader)
                this.shader.uniforms.viewportSize = new Float32Array([
                    this.target.offsetWidth,
                    this.target.offsetHeight,
                ]);
        });

        // for mousemove
        const _position = getComputedStyle(this.target).position;
        const _parent = _position === "absolute" || _position === "fixed" ? this.target.parentNode : this.target;
        if (!_section.isMobile())
            _parent.addEventListener("mousemove", (e) => {
                const rect = this.app.view.getBoundingClientRect();
                this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
                this.mouse.y = ((e.clientY - rect.top) / rect.height) * -2 + 1;
            });

        // for animate
        this.app.ticker.add((delta) => {
            this.animate(delta);
        });

        if (this.debug) console.log("_backgroundGradient", this);

        this.emit("init");
    }

    hexToRGB(hex: string) {
        hex = hex.replace(/^#/, "");
        if (hex.length === 3) {
            hex = hex
                .split("")
                .map((c) => c + c)
                .join("");
        }

        const int = parseInt(hex, 16);
        const r = ((int >> 16) & 255) / 255;
        const g = ((int >> 8) & 255) / 255;
        const b = (int & 255) / 255;

        const toFloat32 = (n: number) => Math.fround(n);

        return [toFloat32(r), toFloat32(g), toFloat32(b), 1];
    }

    lerp(a: number, b: number, t: number) {
        return a + (b - a) * t;
    }

    lerpColorObj(color: any, targetRGBA: number[], t: number) {
        const _comps = color._components;
        const _epsilon = 0.001;

        let _isSame = true;
        for (let i = 0; i < 4; i++) {
            if (Math.abs(_comps[i] - targetRGBA[i]) > _epsilon) {
                _isSame = false;
                break;
            }
        }
        if (_isSame) return;

        _comps[0] = this.lerp(_comps[0], targetRGBA[0], t); // red
        _comps[1] = this.lerp(_comps[1], targetRGBA[1], t); // green
        _comps[2] = this.lerp(_comps[2], targetRGBA[2], t); // blue
        _comps[3] = this.lerp(_comps[3], targetRGBA[3], t); // alpha
        color.setValue(_comps);
    }

    animate(delta: number) {
        this.updateTime += delta * 0.005;

        this.lerpedMouse.x += (this.mouse.x - this.lerpedMouse.x) * 0.05;
        this.lerpedMouse.y += (this.mouse.y - this.lerpedMouse.y) * 0.05;

        this.uniforms.displacement = 1 + Math.sin(this.updateTime) * 0.5;
        this.uniforms.seed = this.updateTime;
        this.uniforms.mousePosition = new Float32Array([this.lerpedMouse.x, this.lerpedMouse.y]);

        // change the color smoothly
        this.lerpColorObj(this.uniforms.color1, this.hexToRGB(this.custom?.color1 ?? this.defaults.color1), 0.04);
        this.lerpColorObj(this.uniforms.color2, this.hexToRGB(this.custom?.color2 ?? this.defaults.color2), 0.04);
        this.lerpColorObj(this.uniforms.color3, this.hexToRGB(this.custom?.color3 ?? this.defaults.color3), 0.04);
        this.lerpColorObj(this.uniforms.color4, this.hexToRGB(this.custom?.color4 ?? this.defaults.color4), 0.04);
        this.lerpColorObj(this.uniforms.color5, this.hexToRGB(this.custom?.color5 ?? this.defaults.color5), 0.04);

        this.emit("animate");
    }

    update(props: uniforms) {
        if (props === this.custom) return;

        // change the current color
        // this.uniforms.color1 = new PIXI.Color(props?.color1 ?? this.defaults.color1);
        // this.uniforms.color2 = new PIXI.Color(props?.color2 ?? this.defaults.color2);
        // this.uniforms.color3 = new PIXI.Color(props?.color3 ?? this.defaults.color3);
        // this.uniforms.color4 = new PIXI.Color(props?.color4 ?? this.defaults.color4);
        // this.uniforms.color5 = new PIXI.Color(props?.color5 ?? this.defaults.color5);

        this.custom = props;

        this.emit("update");
    }

    on(type: string, callback: BackgroundGradientEventCallback) {
        if (!this.listeners[type]) this.listeners[type] = [];
        this.listeners[type].push(callback);
    }

    off(type: string, callback: BackgroundGradientEventCallback) {
        if (!this.listeners[type]) return;
        this.listeners[type] = this.listeners[type].filter((cb) => cb !== callback);
    }

    emit(type: string, event?: MouseEvent) {
        if (this.events[type]) this.events[type](this, event!);
        if (!this.listeners[type]) return;
        this.listeners[type].forEach((callback) => callback(this, event!));
    }
}
